// Capture and persist marketing-attribution data: gclid/UTM, referrer, landing page,
// first-seen timestamp (never overwritten), cumulative page views and time-on-site.
// Stored in localStorage so it survives page navigations.

const TRACKING_KEY = 'autofrance-tracking';
const FIRST_SEEN_KEY = 'autofrance-first-seen';  // separate key — never overwritten once set
const TRACKING_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const TIME_ON_SITE_CAP_MS = 24 * 60 * 60 * 1000;  // cap accumulated foreground time at 24h

interface TrackingData {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  /** Microsoft Ads (Bing) klick-id. */
  msclkid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
  landing_page?: string;
  landing_at?: string;
  page_views?: number;
  time_on_site_ms?: number;
  captured_at: number;
}

function readStored(): TrackingData | null {
  try {
    const raw = localStorage.getItem(TRACKING_KEY);
    if (!raw) return null;
    const parsed: TrackingData = JSON.parse(raw);
    if (Date.now() - parsed.captured_at > TRACKING_EXPIRY_MS) {
      localStorage.removeItem(TRACKING_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(data: TrackingData): void {
  try {
    localStorage.setItem(TRACKING_KEY, JSON.stringify(data));
  } catch {
    // localStorage full or blocked
  }
}

/** Call on every page load to capture tracking params from URL + increment counters */
export function captureTrackingParams(): void {
  if (typeof window === 'undefined') return;

  // Ensure first_seen is recorded exactly once per browser
  if (!localStorage.getItem(FIRST_SEEN_KEY)) {
    try { localStorage.setItem(FIRST_SEEN_KEY, new Date().toISOString()); } catch { /* ignore */ }
  }

  const params = new URLSearchParams(window.location.search);
  const gclid = params.get('gclid');
  const gbraid = params.get('gbraid');
  const wbraid = params.get('wbraid');
  const msclkid = params.get('msclkid');
  const utm_source = params.get('utm_source');
  const utm_medium = params.get('utm_medium');
  const utm_campaign = params.get('utm_campaign');

  const existing = readStored();
  const isFirstAttribution = !existing;

  const referrer = isFirstAttribution && document.referrer && !document.referrer.includes('autofrance.')
    ? document.referrer
    : undefined;
  const landing_page = isFirstAttribution ? window.location.pathname + window.location.search : undefined;
  const landing_at = isFirstAttribution ? new Date().toISOString() : undefined;

  const data: TrackingData = {
    ...(existing || {}),
    ...(gclid && { gclid }),
    ...(gbraid && { gbraid }),
    ...(wbraid && { wbraid }),
    ...(msclkid && { msclkid }),
    ...(utm_source && { utm_source }),
    ...(utm_medium && { utm_medium }),
    ...(utm_campaign && { utm_campaign }),
    ...(referrer && { referrer }),
    ...(landing_page && { landing_page }),
    ...(landing_at && { landing_at }),
    page_views: (existing?.page_views ?? 0) + 1,
    captured_at: Date.now(),
  };

  writeStored(data);
  startTimeOnSiteTracker();
}

let timeTrackerStarted = false;
let visibleSince = 0;

function startTimeOnSiteTracker(): void {
  if (timeTrackerStarted) return;
  timeTrackerStarted = true;

  visibleSince = document.visibilityState === 'visible' ? Date.now() : 0;

  const flush = () => {
    if (!visibleSince) return;
    const delta = Date.now() - visibleSince;
    visibleSince = 0;
    const stored = readStored();
    if (!stored) return;
    const total = Math.min((stored.time_on_site_ms ?? 0) + delta, TIME_ON_SITE_CAP_MS);
    writeStored({ ...stored, time_on_site_ms: total, captured_at: Date.now() });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      visibleSince = Date.now();
    } else {
      flush();
    }
  });
  // pagehide fires reliably on tab close + back-forward navigation
  window.addEventListener('pagehide', flush);
}

/** Get stored tracking data (for checkout). Includes live document.referrer fallback
 * and the first_seen_at timestamp from its dedicated key. */
export function getTrackingData(): Omit<TrackingData, 'captured_at'> & { first_seen_at?: string } | null {
  if (typeof window === 'undefined') return null;

  const stored = readStored();
  const { captured_at: _, ...rest } = stored || ({} as TrackingData);

  // Live document.referrer fallback for returning visitors
  if (!rest.referrer && document.referrer && !document.referrer.includes('autofrance.')) {
    rest.referrer = document.referrer;
  }

  let first_seen_at: string | undefined;
  try {
    first_seen_at = localStorage.getItem(FIRST_SEEN_KEY) || undefined;
  } catch {
    // ignore
  }

  // Flush in-progress time-on-site so checkout has the latest value
  if (visibleSince && stored) {
    const delta = Date.now() - visibleSince;
    rest.time_on_site_ms = Math.min((stored.time_on_site_ms ?? 0) + delta, TIME_ON_SITE_CAP_MS);
  }

  const merged = { ...rest, ...(first_seen_at && { first_seen_at }) };
  return Object.keys(merged).length > 0 ? merged : null;
}
